import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Collapse, FormControlLabel, Slider, Switch, ToggleButton, Tooltip, Typography, Alert } from '@mui/material'
import { getShadowOptions } from './photoReviewApi'

// Photoroom's eight named directions laid out as a compass around the piece:
// "behind" is away from the camera, so it sits at the top. The centre cell is
// automatic (no override).
const COMPASS = [
    ['behindLeft', '↖'], ['behind', '↑'], ['behindRight', '↗'],
    ['left', '←'], [null, 'A'], ['right', '→'],
    ['frontLeft', '↙'], ['front', '↓'], ['frontRight', '↘'],
]

// Photoroom's subjectPoseOverride: 0 = lying flat, 90 = upright. The slider
// snaps to these (80 is today's standing preset); only the ends are worded.
const POSE_PRESETS = [0, 20, 45, 70, 80, 90]

// One shot's one-off shadow settings: Photoroom again on this version's
// stored Gemini image, nothing else re-run, the presets untouched. Prefilled
// from the slot's preset every time it opens. `fromUpload`: the version is
// Daniel's uploaded edit, which Photoroom redoes from instead (server #148).
export default function ShadowControl({ photoId, fetchResource, onSubmit, disabled, fromUpload = false }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState(null)
    const [direction, setDirection] = useState(null)
    const [pose, setPose] = useState(80)
    const [poseAuto, setPoseAuto] = useState(true)
    const [softness, setSoftness] = useState(0.8)

    async function toggle() {
        if (open) { setOpen(false); return }
        setOptions(null)
        setOpen(true)
        const response = await getShadowOptions(fetchResource, photoId)
        if (response.error) { setOptions({ failed: true }); return }
        const { defaults } = response
        setDirection(defaults.direction)
        setPoseAuto(defaults.pose === null)
        setPose(defaults.pose ?? 80)
        setSoftness(defaults.softness)
        setOptions(response)
    }

    function submit() {
        setOpen(false)
        onSubmit({ direction, pose: poseAuto ? null : pose, softness })
    }

    const ready = options && !options.failed
    const allowed = new Set(ready ? options.directions : [])

    return (
        <>
            <Button variant='outlined' disabled={disabled} aria-expanded={open} onClick={toggle}>{t('photoReview.shadow.button')}</Button>
            <Collapse in={open && !disabled} unmountOnExit sx={{ flexBasis: '100%' }}>
                <Box sx={{ mt: 1, p: 2, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
                    {options?.failed && <Alert severity='error' sx={{ flexBasis: '100%' }}>{t('photoReview.shadow.loadFailed')}</Alert>}
                    {ready && !options.available && <Alert severity='info' sx={{ flexBasis: '100%' }}>{t('photoReview.shadow.unavailable')}</Alert>}
                    {ready && (
                        <>
                            <Box>
                                <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>{t('photoReview.shadow.direction')}</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 40px)', gap: 0.5 }}>
                                    {COMPASS.filter(([value]) => value === null || allowed.has(value)).map(([value, glyph]) => {
                                        const name = value === null ? t('photoReview.shadow.directionAuto') : t(`photoReview.shadow.directions.${value}`)
                                        return (
                                            <Tooltip key={value ?? 'auto'} title={name}>
                                                <ToggleButton
                                                    value={value ?? 'auto'}
                                                    size='small'
                                                    aria-label={name}
                                                    selected={direction === value}
                                                    onChange={() => setDirection(value)}
                                                    sx={{ width: 40, height: 40, gridColumn: `${(COMPASS.findIndex(([v]) => v === value) % 3) + 1}`, fontSize: value === null ? 12 : 18 }}
                                                >
                                                    {glyph}
                                                </ToggleButton>
                                            </Tooltip>
                                        )
                                    })}
                                </Box>
                            </Box>
                            <Box sx={{ flex: '1 1 220px', minWidth: 200, maxWidth: 360 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                        {t('photoReview.shadow.pose')}{!poseAuto && ` ${pose}°`}
                                    </Typography>
                                    <FormControlLabel
                                        control={<Switch size='small' checked={poseAuto} onChange={e => setPoseAuto(e.target.checked)} inputProps={{ 'aria-label': t('photoReview.shadow.poseAuto') }} />}
                                        label={t('photoReview.shadow.automatic')}
                                    />
                                </Box>
                                <Slider
                                    value={pose}
                                    onChange={(_, value) => setPose(value)}
                                    min={0}
                                    max={90}
                                    step={null}
                                    disabled={poseAuto}
                                    marks={POSE_PRESETS.map(value => ({
                                        value,
                                        label: value === 0 ? t('photoReview.shadow.flat') : value === 90 ? t('photoReview.shadow.upright') : undefined,
                                    }))}
                                    getAriaLabel={() => t('photoReview.shadow.pose')}
                                    sx={{ mx: 1, width: 'calc(100% - 16px)' }}
                                />
                                <Typography variant='body2' sx={{ fontWeight: 600, mt: 1 }}>{t('photoReview.shadow.softness')} {softness}</Typography>
                                <Slider
                                    value={softness}
                                    onChange={(_, value) => setSoftness(value)}
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    marks={[{ value: 0, label: t('photoReview.shadow.sharp') }, { value: 1, label: t('photoReview.shadow.soft') }]}
                                    getAriaLabel={() => t('photoReview.shadow.softness')}
                                    sx={{ mx: 1, width: 'calc(100% - 16px)' }}
                                />
                            </Box>
                        </>
                    )}
                    <Box sx={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                        <Button variant='contained' disabled={!ready || !options.available} onClick={submit}>{t('photoReview.shadow.submit')}</Button>
                        <Typography variant='caption' color='text.secondary'>{t(fromUpload ? 'photoReview.shadow.manualNote' : 'photoReview.shadow.note')}</Typography>
                    </Box>
                </Box>
            </Collapse>
        </>
    )
}
